/**
 * WebAuthn / FIDO2 Fingerprint Authentication Utilities
 *
 * Uses the Web Authentication API (navigator.credentials) to register
 * and authenticate via biometrics (fingerprint, Windows Hello, etc.).
 */

const BASE_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Check if WebAuthn is supported in the current browser.
 */
export function isWebAuthnSupported(): boolean {
  return (
    !!window.PublicKeyCredential &&
    typeof navigator.credentials !== "undefined"
  );
}

/**
 * Check if a platform authenticator (fingerprint/Windows Hello) is available.
 * On mobile, we relax the check since most mobile browsers support WebAuthn
 * via screen lock biometrics but may not report isUserVerifyingPlatformAuthenticatorAvailable correctly.
 */
export async function isPlatformAuthAvailable(): Promise<boolean> {
  if (!isWebAuthnSupported()) return false;
  try {
    const available = await PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable();
    if (available) return true;

    // Some desktops report false even though WebAuthn works (e.g. Windows Hello not configured but security key available)
    // On mobile devices, WebAuthn is generally supported via screen lock
    const isMobile = /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
    if (isMobile) return true;

    // Check conditional mediation (passkeys) support as fallback
    const publicKeyCredentialWithConditional = PublicKeyCredential as typeof PublicKeyCredential & {
      isConditionalMediationAvailable?: () => Promise<boolean>;
    };
    if (typeof publicKeyCredentialWithConditional.isConditionalMediationAvailable === "function") {
      const conditional = await publicKeyCredentialWithConditional.isConditionalMediationAvailable();
      if (conditional) return true;
    }

    return false;
  } catch {
    return false;
  }
}

// --- Helpers to convert between ArrayBuffer and Base64URL ---

function bufferToBase64url(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  let binary = "";
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

function base64urlToBuffer(base64url: string): ArrayBuffer {
  const base64 = base64url.replace(/-/g, "+").replace(/_/g, "/");
  const padLen = (4 - (base64.length % 4)) % 4;
  const padded = base64 + "=".repeat(padLen);
  const binary = atob(padded);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes.buffer;
}

// ============================================================
// REGISTRATION (Enable Fingerprint)
// ============================================================

/**
 * Step 1: Get registration options from the server.
 * Called when user clicks "Enable Fingerprint Login".
 */
async function getRegistrationOptions(token: string): Promise<any> {
  const res = await fetch(`${BASE_URL}/admin/auth/webauthn/register-options`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
  });
  if (!res.ok) throw new Error("Failed to get registration options");
  return res.json();
}

/**
 * Step 2: Create credentials using the browser's WebAuthn API
 * and send the result to the server for verification.
 */
export async function registerFingerprint(token: string): Promise<{ success: boolean; message: string }> {
  // 1. Get challenge & options from server
  const optionsRes = await getRegistrationOptions(token);
  const options = optionsRes.data?.options || optionsRes.data;
  const sessionId = optionsRes.data?.sessionId;

  // 2. Convert server response to proper format
  const publicKeyOptions: PublicKeyCredentialCreationOptions = {
    challenge: base64urlToBuffer(options.challenge),
    rp: {
      name: options.rp.name,
      id: options.rp.id,
    },
    user: {
      id: base64urlToBuffer(options.user.id),
      name: options.user.name,
      displayName: options.user.displayName,
    },
    pubKeyCredParams: options.pubKeyCredParams,
    timeout: options.timeout || 60000,
    attestation: options.attestation || "none",
    authenticatorSelection: {
      authenticatorAttachment: "platform", // Force platform (fingerprint/Windows Hello)
      userVerification: "required",
      residentKey: "required",
      requireResidentKey: true,
    },
    excludeCredentials: (options.excludeCredentials || []).map((c: any) => ({
      ...c,
      id: base64urlToBuffer(c.id),
    })),
  };

  // 3. Browser prompts for fingerprint
  let credential: PublicKeyCredential;
  try {
    credential = (await navigator.credentials.create({
      publicKey: publicKeyOptions,
    })) as PublicKeyCredential;
  } catch (err: any) {
    if (err.name === "InvalidStateError") {
      throw new Error("Fingerprint is already registered on this device. You can use it to login.");
    }
    if (err.name === "SecurityError") {
      throw new Error("This device/browser cannot register fingerprint with the current server configuration. Make sure you're on the correct URL.");
    }
    if (err.name === "NotAllowedError") {
      throw new Error("Fingerprint registration was cancelled or timed out.");
    }
    throw err;
  }

  if (!credential) throw new Error("Fingerprint registration was cancelled");

  const attestationResponse = credential.response as AuthenticatorAttestationResponse;

  // 4. Send credential to server for verification & storage
  const verifyRes = await fetch(`${BASE_URL}/admin/auth/webauthn/register-verify`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({
      id: credential.id,
      rawId: bufferToBase64url(credential.rawId),
      type: credential.type,
      response: {
        attestationObject: bufferToBase64url(attestationResponse.attestationObject),
        clientDataJSON: bufferToBase64url(attestationResponse.clientDataJSON),
      },
      sessionId,
    }),
  });

  if (!verifyRes.ok) {
    const err = await verifyRes.json().catch(() => ({}));
    throw new Error(err.message || "Registration verification failed");
  }

  const result = await verifyRes.json();
  return { success: true, message: result.message || "Fingerprint registered successfully" };
}

// ============================================================
// AUTHENTICATION (Login with Fingerprint)
// ============================================================

/**
 * Step 1: Get authentication challenge from server.
 */
async function getAuthenticationOptions(): Promise<any> {
  const res = await fetch(`${BASE_URL}/admin/auth/webauthn/login-options`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
  });
  if (!res.ok) throw new Error("Failed to get authentication options");
  return res.json();
}

/**
 * Full login flow using fingerprint.
 * Returns the server response (token, user data) on success.
 */
export async function loginWithFingerprint(fcmToken?: string | null): Promise<any> {
  // 1. Get challenge from server
  const optionsRes = await getAuthenticationOptions();
  const options = optionsRes.data?.options || optionsRes.data;
  const userId = optionsRes.data?.userId ?? null;
  const sessionId = optionsRes.data?.sessionId;

  // 2. Build assertion request
  const publicKeyOptions: PublicKeyCredentialRequestOptions = {
    challenge: base64urlToBuffer(options.challenge),
    timeout: options.timeout || 60000,
    rpId: options.rpId,
    userVerification: "required",
    allowCredentials: (options.allowCredentials || []).map((c: any) => ({
      ...c,
      id: base64urlToBuffer(c.id),
      type: "public-key",
    })),
  };

  // 3. Browser prompts for fingerprint
  let assertion: PublicKeyCredential;
  try {
    assertion = (await navigator.credentials.get({
      publicKey: publicKeyOptions,
    })) as PublicKeyCredential;
  } catch (err: any) {
    if (err.name === "SecurityError") {
      throw new Error("Fingerprint login is not available on this URL. Please use the correct website address.");
    }
    if (err.name === "NotAllowedError") {
      throw new Error("Fingerprint login was cancelled or timed out.");
    }
    throw err;
  }

  if (!assertion) throw new Error("Fingerprint authentication was cancelled");

  const assertionResponse = assertion.response as AuthenticatorAssertionResponse;

  // 4. Send assertion to server for verification
  const verifyRes = await fetch(`${BASE_URL}/admin/auth/webauthn/login-verify`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      id: assertion.id,
      rawId: bufferToBase64url(assertion.rawId),
      type: assertion.type,
      response: {
        authenticatorData: bufferToBase64url(assertionResponse.authenticatorData),
        clientDataJSON: bufferToBase64url(assertionResponse.clientDataJSON),
        signature: bufferToBase64url(assertionResponse.signature),
        userHandle: assertionResponse.userHandle
          ? bufferToBase64url(assertionResponse.userHandle)
          : null,
      },
      fcmToken: fcmToken || undefined,
      userId,
      sessionId,
    }),
  });

  if (!verifyRes.ok) {
    const err = await verifyRes.json().catch(() => ({}));
    throw new Error(err.message || "Fingerprint authentication failed");
  }

  return verifyRes.json();
}
